import { ref, set, get, remove, onValue, query, orderByChild, startAt, endAt, push } from 'firebase/database';
import { db } from '../lib/firebase';
import { User } from '../types';
import { sendNotification } from './notificationService';

export interface SocialUser extends Partial<User> {
  id: string;
  username?: string;
  profilePhotoUrl?: string;
  streak?: number;
  daysWithoutStudy?: number;
}

export const getEffectiveStreak = (data: any): number => {
  if (!data) return 0;
  let daysWithoutStudy = 0;
  const hasStudied = data.streak > 0 || data.wordsLearned > 0 || (data.practiceHistory && Object.keys(data.practiceHistory).length > 0);
  if (data.lastPractice && hasStudied) {
    const lastDate = new Date(data.lastPractice);
    lastDate.setHours(0, 0, 0, 0);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    daysWithoutStudy = Math.round((now.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));
  }
  return daysWithoutStudy > 1 ? 0 : (data.streak || 0);
};

// Check if a friend request exists
export const checkFriendRequestStatus = async (currentUserId: string, targetUserId: string) => {
  const sentReqRef = ref(db, `social/friendRequests/${targetUserId}/${currentUserId}`);
  const sentSnap = await get(sentReqRef);
  if (sentSnap.exists()) return 'sent';

  const rcvReqRef = ref(db, `social/friendRequests/${currentUserId}/${targetUserId}`);
  const rcvSnap = await get(rcvReqRef);
  if (rcvSnap.exists()) return 'received';

  const friendRef = ref(db, `social/friends/${currentUserId}/${targetUserId}`);
  const friendSnap = await get(friendRef);
  if (friendSnap.exists()) return 'friends';

  return 'none';
};

export const sendFriendRequest = async (currentUserId: string, targetUserId: string) => {
  if (currentUserId === targetUserId) return;
  const reqRef = ref(db, `social/friendRequests/${targetUserId}/${currentUserId}`);
  const sentReqRef = ref(db, `social/sentFriendRequests/${currentUserId}/${targetUserId}`);
  await Promise.all([
    set(reqRef, true),
    set(sentReqRef, true)
  ]);

  const currentUserSnap = await get(ref(db, `users/${currentUserId}`));
  const currentUser = currentUserSnap.val();
  
  if (currentUser) {
    await sendNotification(targetUserId, {
      type: 'friend_request',
      message: `${currentUser.username} sent you a friend request!`,
      fromUserId: currentUserId,
      fromUserName: currentUser.username,
      fromUserPhoto: currentUser.profilePhotoUrl
    });
  }
};

export const acceptFriendRequest = async (currentUserId: string, senderId: string) => {
  const reqRef = ref(db, `social/friendRequests/${currentUserId}/${senderId}`);
  const sentReqRef = ref(db, `social/sentFriendRequests/${senderId}/${currentUserId}`);
  const friendRef1 = ref(db, `social/friends/${currentUserId}/${senderId}`);
  const friendRef2 = ref(db, `social/friends/${senderId}/${currentUserId}`);
  
  await Promise.all([
    remove(reqRef),
    remove(sentReqRef),
    set(friendRef1, true),
    set(friendRef2, true)
  ]);

  const currentUserSnap = await get(ref(db, `users/${currentUserId}`));
  const currentUser = currentUserSnap.val();
  
  if (currentUser) {
    await sendNotification(senderId, {
      type: 'friend_request_accepted',
      message: `${currentUser.username} accepted your friend request!`,
      fromUserId: currentUserId,
      fromUserName: currentUser.username,
      fromUserPhoto: currentUser.profilePhotoUrl
    });
  }
};

export const rejectFriendRequest = async (currentUserId: string, senderId: string) => {
  const reqRef = ref(db, `social/friendRequests/${currentUserId}/${senderId}`);
  const sentReqRef = ref(db, `social/sentFriendRequests/${senderId}/${currentUserId}`);
  await Promise.all([
    remove(reqRef),
    remove(sentReqRef)
  ]);
};

export const removeFriend = async (currentUserId: string, targetUserId: string) => {
  const friendRef1 = ref(db, `social/friends/${currentUserId}/${targetUserId}`);
  const friendRef2 = ref(db, `social/friends/${targetUserId}/${currentUserId}`);
  
  await Promise.all([
    remove(friendRef1),
    remove(friendRef2)
  ]);
};

export const cancelFriendRequest = async (currentUserId: string, targetUserId: string) => {
  const reqRef = ref(db, `social/friendRequests/${targetUserId}/${currentUserId}`);
  const sentReqRef = ref(db, `social/sentFriendRequests/${currentUserId}/${targetUserId}`);
  await Promise.all([
    remove(reqRef),
    remove(sentReqRef)
  ]);
};

export const getFriends = (userId: string, callback: (users: SocialUser[]) => void) => {
  const friendsRef = ref(db, `social/friends/${userId}`);
  return onValue(friendsRef, async (snapshot) => {
    const friendsDict = snapshot.val() || {};
    const friendIds = Object.keys(friendsDict);
    
    if (friendIds.length === 0) {
      callback([]);
      return;
    }
    
    const users: SocialUser[] = [];
    for (const id of friendIds) {
      const userRef = ref(db, `users/${id}`);
      const userSnap = await get(userRef);
      if (userSnap.exists()) {
        const data = userSnap.val();
        
        let daysWithoutStudy = 0;
        // Check if user has ever studied to avoid counting days since account creation if they never studied
        const hasStudied = data.streak > 0 || data.wordsLearned > 0 || (data.practiceHistory && Object.keys(data.practiceHistory).length > 0);
        
        if (data.lastPractice && hasStudied) {
          const lastDate = new Date(data.lastPractice);
          lastDate.setHours(0, 0, 0, 0);
          const now = new Date();
          now.setHours(0, 0, 0, 0);
          daysWithoutStudy = Math.round((now.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));
        } else if (!hasStudied) {
          daysWithoutStudy = -1;
        }
        
        users.push({
          id,
          username: data.username,
          profilePhotoUrl: data.profilePhotoUrl,
          streak: daysWithoutStudy > 1 ? 0 : (data.streak || 0),
          daysWithoutStudy
        });
      }
    }
    callback(users);
  });
};

export const getFriendRequests = (userId: string, callback: (users: SocialUser[]) => void) => {
  const reqsRef = ref(db, `social/friendRequests/${userId}`);
  return onValue(reqsRef, async (snapshot) => {
    const reqsDict = snapshot.val() || {};
    const senderIds = Object.keys(reqsDict);
    
    if (senderIds.length === 0) {
      callback([]);
      return;
    }
    
    const users: SocialUser[] = [];
    for (const id of senderIds) {
      const userRef = ref(db, `users/${id}`);
      const userSnap = await get(userRef);
      if (userSnap.exists()) {
        const data = userSnap.val();
        users.push({
          id,
          username: data.username,
          profilePhotoUrl: data.profilePhotoUrl,
          streak: getEffectiveStreak(data)
        });
      }
    }
    callback(users);
  });
};

export const getSentFriendRequests = (userId: string, callback: (users: SocialUser[]) => void) => {
  const sentReqsRef = ref(db, `social/sentFriendRequests/${userId}`);
  return onValue(sentReqsRef, async (snapshot) => {
    const reqsDict = snapshot.val() || {};
    const targetIds = Object.keys(reqsDict);
    
    if (targetIds.length === 0) {
      callback([]);
      return;
    }
    
    const users: SocialUser[] = [];
    for (const id of targetIds) {
      const userRef = ref(db, `users/${id}`);
      const userSnap = await get(userRef);
      if (userSnap.exists()) {
        const data = userSnap.val();
        users.push({
          id,
          username: data.username,
          profilePhotoUrl: data.profilePhotoUrl,
          streak: getEffectiveStreak(data)
        });
      }
    }
    callback(users);
  });
};

export const searchUsers = (searchQuery: string, currentUserId: string, callback: (users: (SocialUser & { friendStatus: string })[]) => void) => {
  if (!searchQuery || searchQuery.length < 2) {
    callback([]);
    return () => {};
  }
  
  const usersRef = query(ref(db, 'users'), orderByChild('username'), startAt(searchQuery), endAt(searchQuery + '\uf8ff'));
  
  return onValue(usersRef, async (snapshot) => {
    const data = snapshot.val() || {};
    const users: (SocialUser & { friendStatus: string })[] = [];
    
    // Get all friends and requests for the current user to map status efficiently
    const [friendsSnap, rcvSnap, sentSnap] = await Promise.all([
      get(ref(db, `social/friends/${currentUserId}`)),
      get(ref(db, `social/friendRequests/${currentUserId}`)),
      get(ref(db, `social/sentFriendRequests/${currentUserId}`))
    ]);
    
    const friends = friendsSnap.val() || {};
    const received = rcvSnap.val() || {};
    const sent = sentSnap.val() || {}; 
    
    for (const [id, val] of Object.entries<any>(data)) {
      if (id !== currentUserId) {
        let status = 'none';
        if (friends[id]) status = 'friends';
        else if (received[id]) status = 'received';
        else if (sent[id]) status = 'sent';
        
        users.push({
          id,
          username: val.username,
          profilePhotoUrl: val.profilePhotoUrl,
          streak: getEffectiveStreak(val),
          friendStatus: status
        });
      }
    }
    callback(users);
  });
};

export const getUserProfileStats = async (userId: string) => {
  // Fetch from global stats or specific user nodes
  const [userSnap, crimeGlobalSnap, statsSnap] = await Promise.all([
    get(ref(db, `users/${userId}`)),
    get(ref(db, `crimeGameLeaderboard/global/users/${userId}`)),
    get(ref(db, `userStats/${userId}`))
  ]);

  const user = userSnap.val() || {};
  const crimeStats = crimeGlobalSnap.val() || {};
  const trainingStats = statsSnap.val() || {};

  // For Word of the Day, we check the cumulative count in userStats
  const wordDayCount = trainingStats.wordDayCount || 0;

  // Calculate days studied by checking totalDaysStudied or practice history length
  const practiceHistory = user.practiceHistory || {};
  let daysStudied = user.totalDaysStudied;
  if (daysStudied === undefined) {
    daysStudied = Math.max(Object.keys(practiceHistory).length, user.streak || 0);
  }
  
  const hasEverStudied = daysStudied > 0 || crimeStats.wins > 0 || trainingStats.completedSessions > 0 || wordDayCount > 0 || user.wordsLearned > 0;
  
  // Calculate days without study (lastPractice)
  let daysWithoutStudy = 0;
  if (user.lastPractice && hasEverStudied) {
    const lastDate = new Date(user.lastPractice);
    lastDate.setHours(0, 0, 0, 0);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    daysWithoutStudy = Math.round((now.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));
  } else {
    // If no last practice or they never played, set days to -1
    daysWithoutStudy = -1;
  }

  return {
    user: {
      id: userId,
      username: user.username || 'Learner',
      profilePhotoUrl: user.profilePhotoUrl,
      streak: daysWithoutStudy > 1 ? 0 : (user.streak || 0),
      totalPoints: Math.round((user.totalPoints || 0) + (user.score || 0) + (user.memoryScore || 0))
    },
    stats: {
      daysStudied,
      crimeGamesSolved: crimeStats.wins || 0,
      dailyTrainingDone: trainingStats.completedSessions || 0,
      wordOfDayDone: wordDayCount
    },
    daysWithoutStudy
  };
};
